// =============================================================================
// Module Core - Access Control Store (Global Zustand)
// Store partagé pour la gestion des accès et permissions
// =============================================================================

import { create } from 'zustand';
import { useCallback, useEffect } from 'react';
import {
  type ModuleCode,
  type AppRole,
  type ModuleDefinition,
  canRoleSeeModule,
  getVisibleModules,
  canManage,
} from '../access-control/definitions';
import {
  fetchCurrentUserModuleAccess,
  hasModuleAccess as checkModuleAccessRPC,
} from '../data/moduleAccessRepository';
import { getUserRole } from '../data/roleRepository';

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL STORE
// ─────────────────────────────────────────────────────────────────────────────

interface AccessControlGlobalState {
  currentOrgId: string | null;
  currentUserRole: AppRole | null;
  currentUserModuleAccess: ModuleCode[];
  loading: boolean;
  error: string | null;
  _loadPromise: Promise<void> | null;
}

interface AccessControlActions {
  loadCurrentUserAccess: (orgId: string) => Promise<void>;
  clearAccess: () => void;
}

const useAccessControlGlobal = create<AccessControlGlobalState & AccessControlActions>((set, get) => ({
  currentOrgId: null,
  currentUserRole: null,
  currentUserModuleAccess: [],
  loading: false,
  error: null,
  _loadPromise: null,

  loadCurrentUserAccess: async (targetOrgId: string) => {
    // Avoid duplicate loads for same org
    const current = get();
    if (current._loadPromise && current.currentOrgId === targetOrgId && current.loading) {
      return current._loadPromise;
    }

    const promise = (async () => {
      set({ loading: true, error: null, currentOrgId: targetOrgId });

      try {
        const [roleCode, moduleAccess] = await Promise.all([
          getUserRole(targetOrgId),
          fetchCurrentUserModuleAccess(targetOrgId),
        ]);

        let mappedRole: AppRole | null = null;
        if (roleCode && ['owner', 'admin', 'member', 'guest'].includes(roleCode)) {
          mappedRole = roleCode as AppRole;
        }

        set({
          currentOrgId: targetOrgId,
          currentUserRole: mappedRole,
          currentUserModuleAccess: moduleAccess as ModuleCode[],
          loading: false,
          error: null,
          _loadPromise: null,
        });
      } catch (err) {
        console.error('Error loading user access:', err);
        set({
          loading: false,
          error: 'Erreur lors du chargement des accès',
          _loadPromise: null,
        });
      }
    })();

    set({ _loadPromise: promise });
    return promise;
  },

  clearAccess: () => {
    set({
      currentOrgId: null,
      currentUserRole: null,
      currentUserModuleAccess: [],
      loading: false,
      error: null,
      _loadPromise: null,
    });
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC HOOK (backward-compatible API)
// ─────────────────────────────────────────────────────────────────────────────

export function useAccessControlStore(orgId: string | null = null) {
  const state = useAccessControlGlobal();

  // Derived helpers
  const canSeeModule = useCallback(
    (moduleCode: ModuleCode): boolean => {
      return canRoleSeeModule(
        state.currentUserRole,
        moduleCode,
        state.currentUserModuleAccess
      );
    },
    [state.currentUserRole, state.currentUserModuleAccess]
  );

  const getVisibleModulesForUser = useCallback((): ModuleDefinition[] => {
    return getVisibleModules(state.currentUserRole, state.currentUserModuleAccess);
  }, [state.currentUserRole, state.currentUserModuleAccess]);

  const canManageAspect = useCallback(
    (aspect: 'members' | 'roles' | 'modules'): boolean => {
      return canManage(state.currentUserRole, aspect);
    },
    [state.currentUserRole]
  );

  const checkModuleAccess = useCallback(
    async (moduleCode: string): Promise<boolean> => {
      if (!state.currentOrgId) return false;
      return checkModuleAccessRPC(state.currentOrgId, moduleCode);
    },
    [state.currentOrgId]
  );

  return {
    // State
    currentOrgId: state.currentOrgId,
    currentUserRole: state.currentUserRole,
    currentUserModuleAccess: state.currentUserModuleAccess,
    loading: state.loading,
    error: state.error,

    // Actions
    loadCurrentUserAccess: state.loadCurrentUserAccess,
    canSeeModule,
    getVisibleModules: getVisibleModulesForUser,
    canManage: canManageAspect,
    checkModuleAccess,
    clearAccess: state.clearAccess,
  };
}
